import { useState, useMemo, useEffect } from 'react';
import { Table, Form, Button, InputGroup, Row, Col, Pagination, Dropdown, DropdownButton } from 'react-bootstrap';
import { getTransactions, getTransactionLetter } from '../utils/api';
import Header from './Header';
import Loader from './Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faBoxArchive, faTrashCan, faArrowsTurnToDots, faFilter, faBan, faCircleCheck, faCheckDouble, faX, faFilePdf, faStop, faTableList, faCheck } from '@fortawesome/free-solid-svg-icons';
import ShowMenuButton from './ShowMenuButton';
import TransactionRequestDocumentsModal from './Modals/TransactionRequestDocumentsModal';
import CreateEditTransactionModal from './Modals/CreateEditTransactionModal';
import { formatDateToMMDDYYYYWithTime, formatDateToMMDDYYYY, tableSort, assetClassToIcon } from '../utils/helpers';
import ViewTransactionModal from './Modals/ViewTransactionModal';
import { useDataStore } from '../Store';
import Error from './Error';
import ArchiveTransactionModal from './Modals/ArchiveTransactionModal';


const Transactions = () => {

    const selectedDealer = useDataStore((state) => state.selectedDealer);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedData, setSelectedData] = useState(null);
    const [selectedDataTransactionStatus, setSelectedDataTransactionStatus] = useState(null);

    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState(0);

    const [showCreateTransactionModal, setShowCreateTransactionModal] = useState(false);
    const [showConfirmTransactionModal, setShowConfirmTransactionModal] = useState(false);
    const [showTransactionRequestDocumentsModal, setShowTransactionRequestDocumentsModal] = useState(false);
    const [showArchiveConfirmationModal, setShowArchiveConfirmationModal] = useState(false);

    useEffect(() => {
        fetchData();
    }, [selectedDealer, searchText, statusFilter, page]);


    const fetchData = async () => {
        const data = await getTransactions(selectedDealer, page, 30, searchText, statusFilter);
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };

    const rowClicked = (data) => {
        handleActionClick('view', data);
    }
    const handleActionClick = (name, data) => {

        var n = name?.toLowerCase()?.trim();

        if (n === 'confirm transaction' || n === 'approve transaction' || n === 'view' || n === 'cancel transaction') {
            setSelectedData(data);
            setShowConfirmTransactionModal(true);

            var status = 0;
            if (n === 'confirm transaction')
                status = 2;
            if (n === 'approve transaction')
                status = 3;
            if (n === 'cancel transaction')
                status = 4;
            setSelectedDataTransactionStatus(status);
            return;
        }

        if (n === 'request documents') {
            setSelectedData(data);
            setShowTransactionRequestDocumentsModal(true);
            return;
        }

        if (n === 'archive') {
            setSelectedData(data);
            setShowArchiveConfirmationModal(true);
            return;
        }
    }
    const createTransaction = () => {
        setSelectedData(null);
        setShowCreateTransactionModal(true);
    }

    const buttons = [
        { id: 1, label: 'Create Transaction', variant: 'primary', icon: faPlus, onClick: () => createTransaction() },
    ]

    const lastPage = data.LastPage;


    return (
        <div style={{ padding: 10 }}>
            {loading ? <Loader /> : error ? <Error /> : (
                <>
                    <Header title='Transactions' description="Manage transactions including creating, update workflow status and generating documentation" buttons={buttons} />
                    <Row className="mb-3">
                        <Col md={12}>
                            <InputGroup>
                                <Form.Control
                                    type="text"
                                    placeholder="Search transactions..."
                                    value={searchText}
                                    onChange={(e) => {
                                        setPage(1);
                                        setSearchText(e.target.value)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            setPage(1);
                                            fetchData();
                                        }
                                    }}
                                />
                                <Button variant="outline-secondary" onClick={fetchData}>
                                    Search
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                        <Col>
                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <Table striped bordered hover style={{ minHeight: 190, overflow: 'visible' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Id
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Assets
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Customer
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Titling State
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Created
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    Status
                                                    <DropdownButton
                                                        id="type-filter"
                                                        title={<FontAwesomeIcon icon={faFilter} />}
                                                        variant={'primary'}
                                                        style={{ height: 24 }}
                                                        className="td-filter-dropdown-button"
                                                        disabled={false}
                                                        onSelect={(eventKey) => setStatusFilter(eventKey)}
                                                    >
                                                        {data?.Filters?.find(c => c.Field == "statuses")?.FilterEntries?.map((d, idx) => (
                                                            <Dropdown.Item key={d.Key} eventKey={d.Key} active={statusFilter == d.Key}>{d.Text}</Dropdown.Item>
                                                        ))}
                                                    </DropdownButton>
                                                </div>
                                            </th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.Results?.map((d, idx) => (
                                            <tr key={d.Id}>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Id}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>
                                                    {d?.TransactionAssets?.filter(c => c?.TransactionAssetType?.Id === 2).map((item) => (
                                                        <div key={item?.Id}>{assetClassToIcon(item?.Asset?.AssetClass?.Id)} {item?.Asset?.Year} {item?.Asset?.Make} {item?.Asset?.Model} {item?.Asset?.Vin} (<i>{item?.TransactionAssetType?.Description}</i>)</div>
                                                    ))}
                                                    {d?.TransactionAssets?.filter(c => c?.TransactionAssetType?.Id === 3).length > 0 && <br />}
                                                    {d?.TransactionAssets?.filter(c => c?.TransactionAssetType?.Id === 3).map((item) => (
                                                        <div key={item?.Id}>{assetClassToIcon(item?.Asset?.AssetClass?.Id)} {item?.Asset?.Year} {item?.Asset?.Make} {item?.Asset?.Model} {item?.Asset?.Vin} (<i>{item?.TransactionAssetType?.Description}</i>)</div>
                                                    ))}
                                                    {d?.TransactionAssets?.filter(c => c?.TransactionAssetType?.Id === 1).length > 0 && <br />}
                                                    {d?.TransactionAssets?.filter(c => c?.TransactionAssetType?.Id === 1).map((item) => (
                                                        <div key={item?.Id}>{assetClassToIcon(item?.Asset?.AssetClass?.Id)} {item?.Asset?.Year} {item?.Asset?.Make} {item?.Asset?.Model} {item?.Asset?.Vin} (<i>{item?.TransactionAssetType?.Description}</i>)</div>
                                                    ))}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>
                                                    {d?.TransactionCustomerRoles?.map((item) => (
                                                        <div key={item?.Id}>{item?.Customer?.Name} (<i>{item?.TransactionCustomerRoleType?.Description}</i>)</div>
                                                    ))}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.TitlingState?.Name}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{formatDateToMMDDYYYYWithTime(d?.CreatedDateTime)}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.TransactionStatus?.Description}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle' }}>{<ShowMenuButton menuItems={[
                                                    { icon: <FontAwesomeIcon icon={faArrowsTurnToDots} />, name: 'View', target: d, onClick: handleActionClick },
                                                    { icon: <FontAwesomeIcon icon={faCheck} />, name: 'Confirm Transaction', target: d, onClick: handleActionClick, disabled: (d.TransactionStatus?.Id !== 1) },
                                                    { icon: <FontAwesomeIcon icon={faCheckDouble} />, name: 'Approve Transaction', target: d, onClick: handleActionClick, disabled: (d.TransactionStatus?.Id !== 2) },
                                                    { icon: <FontAwesomeIcon icon={faBan} />, name: 'Cancel Transaction', target: d, onClick: handleActionClick, disabled: (d.TransactionStatus?.Id === 3 || d.TransactionStatus?.Id === 4) },
                                                    { icon: <FontAwesomeIcon icon={faFilePdf} />, name: 'Request Documents', target: d, onClick: handleActionClick },
                                                    { icon: <FontAwesomeIcon icon={faBoxArchive} />, name: 'Archive', target: d, onClick: handleActionClick },
                                                ]} />}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                        <Col className="d-flex justify-content-center">
                            <Pagination>
                                <Pagination.First disabled={page <= 1} onClick={() => setPage(1)} />
                                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                                <Pagination.Item key={page} active={true}>
                                    {page}
                                </Pagination.Item>
                                <Pagination.Next disabled={page >= lastPage} onClick={() => setPage(page + 1)} />
                                <Pagination.Last disabled={page >= lastPage} onClick={() => setPage(lastPage)} />
                            </Pagination>
                        </Col>
                    </Row>

                    <CreateEditTransactionModal
                        show={showCreateTransactionModal}
                        onHide={(bubble) => {
                            setShowCreateTransactionModal(false)
                            if (bubble)
                                fetchData();
                        }}
                    />

                    <ViewTransactionModal
                        transaction={selectedData}
                        transactionStatus={selectedDataTransactionStatus}
                        show={showConfirmTransactionModal}
                        onHide={(bubble) => {
                            setShowConfirmTransactionModal(false)
                            if (bubble)
                                fetchData();
                        }}
                    />

                    <TransactionRequestDocumentsModal
                        transaction={selectedData}
                        show={showTransactionRequestDocumentsModal}
                        onHide={(bubble) => {
                            setShowTransactionRequestDocumentsModal(false)
                            if (bubble)
                                fetchData();
                        }}
                    />

                    <ArchiveTransactionModal
                        show={showArchiveConfirmationModal}
                        onHide={(shouldRefresh) => {
                            setShowArchiveConfirmationModal(false);
                            if (shouldRefresh)
                                fetchData();
                        }}
                        transaction={selectedData}
                    />

                </>
            )
            }
        </div >
    );
};

export default Transactions;
